/**
 * Dossier de l'affaire, toujours à portée de main : témoins (un clic pour
 * interroger), pièces (un clic pour verser), stratégie de son camp (à cocher,
 * gardée dans le navigateur) et faits.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { articlePath } from '@/utils/articleSlug';

import { TrialAvatar, witnessVariant } from './Courtroom';
import { formatTime, readLocal, writeLocal, type Side, type Trial } from './trialTypes';

export type DossierTab = 'witnesses' | 'evidence' | 'strategy' | 'facts';

/**
 * Extrait { numero, code } d'une citation type "Article 1240 du Code civil (...)".
 * Retourne null si le format ne correspond pas — dans ce cas la citation reste
 * du texte simple plutôt qu'un lien fabriqué au hasard.
 */
function parseArticleCitation(raw: string): { numero: string; code: string } | null {
  const noParen = raw.replace(/\(.*$/, '').trim();
  const m = noParen.match(/^Article\s+(.+?)\s+d(?:u|es|e\s+l['’]|e\s+la)\s+(.+)$/i);
  if (!m) return null;
  return { numero: m[1].trim(), code: m[2].trim() };
}

const SIDE_CHIP: Record<string, string> = {
  prosecution: 'text-[#9b3a2e] bg-[#f6e9e3] dark:text-[#d4a090] dark:bg-[#3a150f]/40',
  defense:     'text-[#3d5a73] bg-[#e9eef1] dark:text-[#9fb2c1] dark:bg-[#18242d]/40',
  neutral:     'text-stone-700 bg-stone-100 dark:text-stone-300 dark:bg-stone-800',
};
const SIDE_RING: Record<string, string> = { prosecution: '#9b3a2e', defense: '#3d5a73', neutral: '#78716c' };

/** Onglets disponibles pour ce lecteur. */
export function dossierTabs(trial: Trial, myRole: Side | null): DossierTab[] {
  const gc = trial.generated_case;
  const points = myRole === 'prosecution' ? gc.key_points_prosecution : myRole === 'defense' ? gc.key_points_defense : [];
  return [
    ...(gc.witnesses?.length ? ['witnesses' as const] : []),
    'evidence',
    ...(points?.length ? ['strategy' as const] : []),
    'facts',
  ];
}

export function CaseDossier({
  trial, myRole, tab, onTab, canQuestion, selectedWitness, onQuestion, canFile, selectedEvidence, onFile, hideTabs = false,
}: {
  trial: Trial;
  myRole: Side | null;
  tab: DossierTab;
  onTab: (t: DossierTab) => void;
  canQuestion: boolean;
  selectedWitness?: string | null;
  onQuestion?: (witnessId: string) => void;
  canFile: boolean;
  selectedEvidence?: string | null;
  onFile?: (evidenceId: string) => void;
  /** Feuille mobile : l'onglet est choisi par la barre du bas. */
  hideTabs?: boolean;
}) {
  const { t } = useTranslation();
  const gc = trial.generated_case;
  const tabs = dossierTabs(trial, myRole);
  const current = tabs.includes(tab) ? tab : tabs[0];
  const tabLabel: Record<DossierTab, string> = {
    witnesses: t('tribunal.court.tabs.witnesses'),
    evidence:  t('tribunal.court.tabs.evidence'),
    strategy:  t('tribunal.court.tabs.strategy'),
    facts:     t('tribunal.court.tabs.facts'),
  };
  const counts: Partial<Record<DossierTab, string>> = {
    witnesses: String(gc.witnesses?.length ?? 0),
    evidence:  trial.evidence_list.length ? String(trial.evidence_list.length) : '',
  };

  return (
    <div className="flex flex-col min-h-0 h-full">
      {!hideTabs && (
        <div role="tablist" aria-label={t('tribunal.court.dossier')} className="shrink-0 grid gap-1 px-3 pt-3 border-b border-border" style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}>
          {tabs.map(id => (
            <button
              key={id}
              role="tab"
              aria-selected={current === id}
              onClick={() => onTab(id)}
              className={`h-11 flex items-center justify-center gap-1.5 text-[13px] transition-colors ${
                current === id ? 'font-bold text-foreground shadow-[inset_0_-2px_0_currentColor]' : 'font-semibold text-muted-foreground hover:text-foreground'
              }`}
            >
              {tabLabel[id]}
              {counts[id] && <span className="text-[11px] font-bold px-1.5 rounded-full bg-muted text-muted-foreground">{counts[id]}</span>}
            </button>
          ))}
        </div>
      )}
      <div role="tabpanel" className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4">
        {current === 'witnesses' && (
          <WitnessList trial={trial} canQuestion={canQuestion} selected={selectedWitness} onQuestion={onQuestion} />
        )}
        {current === 'evidence' && (
          <EvidencePanel trial={trial} myRole={myRole} canFile={canFile} selected={selectedEvidence} onFile={onFile} />
        )}
        {current === 'strategy' && <StrategyChecklist trial={trial} myRole={myRole} />}
        {current === 'facts' && <Facts trial={trial} />}
      </div>
    </div>
  );
}

function WitnessList({
  trial, canQuestion, selected, onQuestion,
}: { trial: Trial; canQuestion: boolean; selected?: string | null; onQuestion?: (id: string) => void }) {
  const { t } = useTranslation();
  const heardAt = new Map<string, string>();
  for (const m of trial.messages) {
    if (m.role === 'witness' && m.metadata?.witness_id) heardAt.set(m.metadata.witness_id, m.created_at);
  }
  return (
    <>
      <p className="mb-3 text-xs text-muted-foreground leading-relaxed">
        {canQuestion ? t('tribunal.court.witness_hint_lawyer') : t('tribunal.court.witness_hint')}
      </p>
      <ul className="flex flex-col gap-2">
        {trial.generated_case.witnesses.map(w => {
          const on = selected === w.id;
          const heard = heardAt.get(w.id);
          const inner = (
            <>
              <span className="rounded-full shrink-0" style={{ boxShadow: `0 0 0 2px ${SIDE_RING[w.side] ?? SIDE_RING.neutral}`, opacity: heard && !on ? 0.6 : 1 }}>
                <TrialAvatar role="witness" variant={witnessVariant(w.id, trial.generated_case.witnesses)} size={44} alt="" />
              </span>
              <span className="flex-1 min-w-0 flex flex-col gap-0.5 text-left">
                <span className="flex items-start gap-2">
                  <span className="font-bold text-sm text-foreground">{w.name}</span>
                  <span className={`ml-auto shrink-0 text-[11px] font-bold px-2 py-0.5 rounded-full ${SIDE_CHIP[w.side] ?? SIDE_CHIP.neutral}`}>
                    {w.side === 'neutral' ? t('tribunal.court.side_neutral') : t(`tribunal.seats.${w.side}`)}
                  </span>
                </span>
                <span className="text-xs text-muted-foreground">{w.role}</span>
                {w.testimony_summary && <span className="text-xs text-foreground/80 leading-relaxed line-clamp-2 mt-0.5">{w.testimony_summary}</span>}
                <span className="flex items-center justify-between mt-1">
                  <span className="text-xs text-muted-foreground">
                    {heard ? t('tribunal.court.heard_at', { time: formatTime(heard) }) : t('tribunal.court.not_heard')}
                  </span>
                  {canQuestion && (
                    <span className={`text-xs font-bold ${on ? 'text-[#3d5f52] dark:text-[#9cbfae]' : 'text-foreground'}`}>
                      {on ? t('tribunal.court.selected') : t('tribunal.court.question_cta')}
                    </span>
                  )}
                </span>
              </span>
            </>
          );
          const cls = `w-full flex items-start gap-3 p-3 rounded-xl transition-colors ${
            on ? 'bg-[#e9f1ec] dark:bg-[#1b2c25]/30 ring-2 ring-[#4f7a6a]' : 'bg-card border border-border'
          }`;
          return (
            <li key={w.id}>
              {canQuestion && onQuestion
                ? <button type="button" onClick={() => onQuestion(w.id)} aria-pressed={on} className={`${cls} hover:border-foreground/30`}>{inner}</button>
                : <div className={cls}>{inner}</div>}
            </li>
          );
        })}
      </ul>
    </>
  );
}

function EvidencePanel({
  trial, myRole, canFile, selected, onFile,
}: { trial: Trial; myRole: Side | null; canFile: boolean; selected?: string | null; onFile?: (id: string) => void }) {
  const { t } = useTranslation();
  const filedTitles = new Set(trial.evidence_list.map(e => e.title.trim().toLowerCase()));
  const available = (trial.generated_case.available_evidence ?? []).filter(e => !filedTitles.has(e.title.trim().toLowerCase()));
  const h = 'mb-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground';
  return (
    <>
      <h3 className={h}>{t('tribunal.court.filed_heading')}</h3>
      {trial.evidence_list.length === 0 ? (
        <p className="mb-5 text-xs text-muted-foreground">{t('tribunal.court.none_filed')}</p>
      ) : (
        <ul className="mb-5 flex flex-col gap-2">
          {trial.evidence_list.map((ev, i) => (
            <li key={ev.id} className="p-3 rounded-xl border border-border bg-card flex flex-col gap-1">
              <span className="flex items-start justify-between gap-2">
                <span className={`font-bold text-sm ${ev.admitted === false ? 'line-through text-muted-foreground' : 'text-foreground'}`}>{ev.title}</span>
                <span className={`shrink-0 text-[11px] font-bold px-2 py-0.5 rounded-full ${
                  ev.admitted === true  ? 'text-[#3d5f52] bg-[#d8e6de] dark:text-[#9cbfae] dark:bg-[#1b2c25]/40' :
                  ev.admitted === false ? 'text-[#7e2c22] bg-[#f0dad0] dark:text-[#d4a090] dark:bg-[#3a150f]/40' :
                                          'text-[#553913] bg-[#f3e3c0] dark:text-[#e0b866] dark:bg-[#33220b]/40'
                }`}>
                  {ev.admitted === true ? t('tribunal.court.admitted') : ev.admitted === false ? t('tribunal.court.rejected') : t('tribunal.court.pending_ruling')}
                </span>
              </span>
              <span className="text-xs text-muted-foreground">
                {t('tribunal.court.exhibit_meta', { n: i + 1, side: t(`tribunal.seats.${ev.submitted_by}`, ev.submitted_by), time: formatTime(ev.created_at) })}
              </span>
            </li>
          ))}
        </ul>
      )}
      {available.length > 0 && (
        <>
          <h3 className={h}>{myRole ? t('tribunal.court.available_heading') : t('tribunal.court.in_file_heading')}</h3>
          <ul className="flex flex-col gap-2">
            {available.map(e => {
              const on = selected === e.id;
              return (
                <li key={e.id} className={`p-3 rounded-xl flex flex-col gap-1.5 ${on ? 'bg-[#e9f1ec] dark:bg-[#1b2c25]/30 ring-2 ring-[#4f7a6a]' : 'bg-muted/40 border border-border'}`}>
                  <span className="font-bold text-sm text-foreground">{e.title}</span>
                  <span className="text-xs text-muted-foreground leading-relaxed">{e.description}</span>
                  {myRole && onFile && (
                    <button
                      type="button"
                      onClick={() => onFile(e.id)}
                      disabled={!canFile}
                      aria-pressed={on}
                      className="self-end h-9 px-3 rounded-lg border border-border bg-background text-xs font-bold text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {on ? t('tribunal.court.selected') : t('tribunal.court.file_cta')}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </>
  );
}

function StrategyChecklist({ trial, myRole }: { trial: Trial; myRole: Side | null }) {
  const { t } = useTranslation();
  const gc = trial.generated_case;
  const points = myRole === 'prosecution' ? gc.key_points_prosecution : gc.key_points_defense;
  const key = `trial_${trial.id}_strategy_${myRole}`;
  const [done, setDone] = useState<number[]>([]);
  useEffect(() => {
    try { setDone(JSON.parse(readLocal(key) || '[]')); } catch { setDone([]); }
  }, [key]);
  const toggle = (i: number) => {
    const next = done.includes(i) ? done.filter(x => x !== i) : [...done, i];
    setDone(next);
    writeLocal(key, JSON.stringify(next));
  };
  const accent = myRole === 'prosecution' ? 'accent-[#9b3a2e]' : 'accent-[#3d5a73]';
  return (
    <>
      <p className="mb-3 text-xs text-muted-foreground leading-relaxed">{t('tribunal.court.strategy_intro')}</p>
      <ul className="flex flex-col gap-1.5">
        {points.map((p, i) => (
          <li key={i}>
            <label className="flex gap-2.5 items-start p-3 rounded-xl bg-muted/40 cursor-pointer">
              <input type="checkbox" checked={done.includes(i)} onChange={() => toggle(i)} className={`mt-0.5 w-4 h-4 shrink-0 ${accent}`} />
              <span className={`text-[13px] leading-relaxed ${done.includes(i) ? 'line-through text-muted-foreground' : 'text-foreground'}`}>{p}</span>
            </label>
          </li>
        ))}
      </ul>
    </>
  );
}

function Facts({ trial }: { trial: Trial }) {
  const { t } = useTranslation();
  const gc = trial.generated_case;
  const h = 'mb-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground';
  return (
    <div className="flex flex-col gap-5 text-sm">
      <section>
        <h3 className={h}>{t('tribunal.court.charges')}</h3>
        <p className="font-semibold text-foreground leading-relaxed">{gc.accusation}</p>
        {gc.charges?.length > 0 && (
          <ul className="mt-2 list-disc pl-5 space-y-1 text-[13px] leading-relaxed">
            {gc.charges.map((c, i) => {
              const parsed = parseArticleCitation(c);
              return (
                <li key={i}>
                  {parsed
                    ? <Link to={articlePath(parsed.code, parsed.numero)} title={t('tribunal.room.view_article')} className="text-[#3d5a73] dark:text-[#8aa3b8] underline decoration-dotted hover:decoration-solid">{c}</Link>
                    : c}
                </li>
              );
            })}
          </ul>
        )}
      </section>
      <section>
        <h3 className={h}>{t('tribunal.court.facts')}</h3>
        <p className="leading-relaxed text-foreground/90 whitespace-pre-line">{gc.facts}</p>
      </section>
      {gc.context && (
        <section>
          <h3 className={h}>{t('tribunal.court.context')}</h3>
          <p className="leading-relaxed text-foreground/90 whitespace-pre-line">{gc.context}</p>
        </section>
      )}
      <section>
        <h3 className={h}>{t('tribunal.room.defendant')}</h3>
        <p className="font-semibold text-foreground">{gc.defendant.name}</p>
        <p className="text-xs text-muted-foreground">{t('tribunal.room.age_profession', { age: gc.defendant.age, profession: gc.defendant.profession })}</p>
        <p className="mt-1 leading-relaxed text-foreground/90">{gc.defendant.background}</p>
      </section>
    </div>
  );
}
